import React, { useState } from 'react'
import { Heading } from '@scentregroup/ui'
import TradingHoursLine from '../trading-hours-line'
import { parseCentreOpeningHours } from '../../helpers/parse-opening-hours'
import { CentreTradingHours } from '../../helpers/parse-opening-hours/types'
import { Box } from '@hub/box'
import { Button } from '@hub/button'
import { ChevronDownSmallIcon, ChevronUpSmallIcon } from '@hub/icon'
import style from './index.module.scss'

export interface TradingHoursListProps {
  heading?: string
  openHoursHeading?: string
  openDaysHeading?: string
  centreTradingHours: CentreTradingHours
  minDays: number
}

function hasHours(
  centreTradingHours: TradingHoursListProps['centreTradingHours']
): boolean {
  return (
    Boolean(centreTradingHours) &&
    Boolean(centreTradingHours.tradingHours) &&
    Boolean(centreTradingHours.timeZone)
  )
}

const TradingHoursList: React.FC<
  React.PropsWithChildren<TradingHoursListProps>
> = ({
  heading,
  openHoursHeading,
  openDaysHeading,
  centreTradingHours,
  minDays,
}): JSX.Element | null => {
  const [showMoreOpeningHours, setShowMoreOpeningHours] = useState(false)

  if (!hasHours(centreTradingHours)) {
    return null
  }
  const openHours = parseCentreOpeningHours(
    centreTradingHours.tradingHours,
    centreTradingHours.timeZone,
    centreTradingHours.features
  )
  const hasColumnHeading = Boolean(openDaysHeading || openHoursHeading)
  const tradingHoursLines = openHours.map(hours => (
    <TradingHoursLine
      openHourDay={hours}
      key={hours.day}
      sx={{ justifyContent: 'space-between' }}
      hoursSx={{ width: 'size-15' }}
    />
  ))

  const ChevronSmallIcon = showMoreOpeningHours
    ? ChevronUpSmallIcon
    : ChevronDownSmallIcon

  return (
    <div>
      {heading && <Heading type="medium">{heading}</Heading>}
      <div className={style.TradingHoursList}>
        {hasColumnHeading && (
          <div className={style.tableHeading}>
            <div>
              {openDaysHeading && (
                <Heading type="extrasmall" className={style.day}>
                  {openDaysHeading}
                </Heading>
              )}
            </div>
            <div>
              {openHoursHeading && (
                <Heading type="extrasmall" className={style.hours}>
                  {openHoursHeading}
                </Heading>
              )}
            </div>
          </div>
        )}

        {showMoreOpeningHours
          ? tradingHoursLines
          : tradingHoursLines.slice(0, minDays)}
        <Box>
          <Button
            type="button"
            variant="transparent"
            colorScheme="licorice"
            onClick={() => setShowMoreOpeningHours(!showMoreOpeningHours)}
            rightIcon={
              <ChevronSmallIcon
                sx={{
                  width: 'size-icon-sm',
                  height: 'size-icon-sm',
                  color: 'licorice',
                }}
              />
            }
          >
            see {showMoreOpeningHours ? 'less' : 'more'} opening hours
          </Button>
        </Box>
      </div>
    </div>
  )
}

export default TradingHoursList
